'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { QueriesKeys } from '@/queries/queriesKeys';
import { useAppSelector } from '@/redux/hooks';
import { cn } from '@/utils/cn';
import { wrapDigitsWithStableWidth } from '@/utils/texts.utils';
import { formatHoursMinutesSeconds, getNextChicagoMidnightIso } from '../utils/dailyBonusRewards.utils';

const getFormattedTimeRemaining = (targetDate: string): string => {
  const diffInMs = Math.max(new Date(targetDate).getTime() - Date.now(), 0);
  const totalSeconds = Math.floor(diffInMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return formatHoursMinutesSeconds({ hours, minutes, seconds }) ?? '00:00:00';
};

const DailyBonusTimer = () => {
  const userSession = useAppSelector(state => state.user.userSession);
  const isGuest = !userSession;
  const queryClient = useQueryClient();
  const timerRef = useRef<HTMLSpanElement>(null);
  const initialTargetDate = useMemo(() => getNextChicagoMidnightIso(), []);

  useEffect(() => {
    let targetDate = initialTargetDate;
    let expiredTargetDate: string | null = null;

    const updateTimer = () => {
      if (!timerRef.current) {
        return;
      }

      if (new Date(targetDate).getTime() <= Date.now()) {
        if (expiredTargetDate !== targetDate) {
          expiredTargetDate = targetDate;
          queryClient.invalidateQueries({ queryKey: [QueriesKeys.rewardsGrouped] });
        }

        targetDate = getNextChicagoMidnightIso();
      }

      timerRef.current.innerHTML = wrapDigitsWithStableWidth(getFormattedTimeRemaining(targetDate));
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);

    return () => clearInterval(intervalId);
  }, [initialTargetDate, queryClient]);

  return (

    <div className={cn(
      'relative gold-border-gradient shrink-0 inline-flex p-px rounded-full',
      !isGuest && 'animate-gold-pulse',
    )}
    >
      <div className="relative flex h-5 items-center justify-center px-1 rounded-[inherit] bg-c900">
        {!isGuest && (
          <div aria-hidden className="animate-gold-inner-glow pointer-events-none absolute inset-0 rounded-[inherit]" />
        )}
        <span
          ref={timerRef}
          suppressHydrationWarning
          className="relative block text-[0.625rem] leading-none font-semibold uppercase text-center whitespace-nowrap text-main-pending"
        >
          --:--:--
        </span>
      </div>
    </div>
  );
};

export default DailyBonusTimer;
